/**
 * @fileoverview Revoke-invitation mutation hook.
 * @description Wraps POST /identity/invitations/{id}/revoke in a TanStack mutation, refreshing the
 * list afterwards whatever the outcome (see useResendInvitation for why).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { USER_DIRECTORY_QUERY_KEY } from '@/features/user-invitations/hooks/useUserInvitationActions'
import { revokeInvitation } from '@/features/user-invitations/services/user-invitations.service'
import { INVITATIONS_QUERY_KEY } from '@/shared/constants/invitations-query-keys.constants'

import type {
  InvitationRevokeReason,
  RevokeInvitationResponse,
} from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

export interface RevokeInvitationVariables {
  invitationId: string
  reason: InvitationRevokeReason
}

/**
 * @description Revokes an invitation.
 * @returns {ReturnType<typeof useMutation<RevokeInvitationResponse, ApiError,
 * RevokeInvitationVariables>>} The mutation object (mutate, isPending, error, ...).
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient()

  return useMutation<RevokeInvitationResponse, ApiError, RevokeInvitationVariables>({
    mutationFn: ({ invitationId, reason }) => revokeInvitation(invitationId, reason),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })
      // The user directory (GET /users) lists the invitee, so refresh it too.
      void queryClient.invalidateQueries({ queryKey: USER_DIRECTORY_QUERY_KEY })
    },
  })
}
